import { createStore, type StateCreator, type StoreApi } from '@bomdisoft/store/vanilla';
import * as React from 'react';

/** Subscribe a component to a vanilla store, optionally through a selector. */
export function useStore<T>(api: StoreApi<T>): T;
export function useStore<T, U>(api: StoreApi<T>, selector: (state: T) => U): U;
export function useStore<T, U>(api: StoreApi<T>, selector?: (state: T) => U): T | U {
  return React.useSyncExternalStore(
    api.subscribe,
    () => (selector ? selector(api.getState()) : api.getState()),
    () => (selector ? selector(api.getInitialState()) : api.getInitialState()),
  );
}

export type UseBoundStore<T> = {
  (): T;
  <U>(selector: (state: T) => U): U;
} & StoreApi<T>;

const createReact = <T>(createState: StateCreator<T>): UseBoundStore<T> => {
  const api = createStore<T>(createState);
  const useBoundStore = ((selector?: (state: T) => unknown) => useStore(api, selector as (state: T) => unknown)) as UseBoundStore<T>;
  return Object.assign(useBoundStore, api);
};

/**
 * React store. Mirrors `zustand`'s `create`, including the curried `create<T>()(init)`
 * form. The returned hook takes an optional selector and carries the vanilla store API
 * (`getState`/`setState`/`subscribe`) as properties.
 */
export function create<T>(): (createState: StateCreator<T>) => UseBoundStore<T>;
export function create<T>(createState: StateCreator<T>): UseBoundStore<T>;
export function create<T>(createState?: StateCreator<T>) {
  return createState ? createReact(createState) : createReact;
}
